import { Typography, Chip, Box, TextField, ButtonBase } from "@mui/material";
import { Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import type { CodeListItem } from "Models/ICodeListDto";
import { Resources, useResource } from "Translations/Resources";

type Props<FormType extends FieldValues, TCode = string> = {
  options: Array<CodeListItem<TCode>>;
  control: Control<FormType, object>;
  name: FieldPath<FormType>;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  selectAllLabel?: string;
};

export const CodeListAutocomplete = <
  FormType extends FieldValues,
  TCode = string,
>({
  options,
  control,
  name,
  label,
  placeholder,
  disabled = false,
  selectAllLabel,
}: Props<FormType, TCode>) => {
  const { t } = useResource();
  const defaultPlaceholder = t(
    Resources.Components.Shared.CodeListAutocomplete.Placeholder,
  );
  const defaultSelectAllLabel = t(
    Resources.Components.Shared.CodeListAutocomplete.SelectAll,
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const selectedValues: Array<TCode> | undefined | null = value;

        return (
          <div>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              {label && (
                <Typography component="label" htmlFor={name}>
                  {label}
                </Typography>
              )}
              <ButtonBase
                onClick={() => {
                  const allCodes = options.map(({ code }) => code);
                  onChange(allCodes);
                }}
                disabled={disabled}
                sx={{
                  fontSize: "0.875rem",
                  textTransform: "none",
                  color: disabled ? "text.secondary" : "primary.main",
                  cursor: disabled ? "default" : "pointer",
                }}
              >
                <Typography fontWeight={500}>
                  {selectAllLabel ?? defaultSelectAllLabel}
                </Typography>
              </ButtonBase>
            </Box>
            <Autocomplete
              id={name}
              multiple
              options={options}
              getOptionLabel={({ name }) => name}
              value={options.filter(({ code }) =>
                selectedValues?.includes(code),
              )}
              onChange={(_, newValues) => {
                onChange(newValues.map(({ code }) => code));
              }}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    selectedValues?.length
                      ? ""
                      : (placeholder ?? defaultPlaceholder)
                  }
                  fullWidth
                  sx={({ colors }) => ({
                    mt: 1,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: error?.message ? "error.main" : "divider",
                      borderRadius: 1,
                    },
                    ...(disabled && {
                      "& .MuiOutlinedInput-root.Mui-disabled": {
                        backgroundColor: `${colors.gray}80`,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.border,
                        },
                      },
                    }),
                  })}
                  error={!!error?.message}
                  helperText={error?.message || " "}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map(({ name, code }, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={`${name}-${code}`}
                    label={name}
                    deleteIcon={<CloseIcon />}
                    onDelete={() => {
                      const newValues = value
                        .filter((_, i) => i !== index)
                        .map(({ code }) => code);
                      onChange(newValues);
                    }}
                  />
                ))
              }
              renderOption={(props, { name }) => (
                <Box component="li" display="flex" gap={1} {...props}>
                  <Typography fontWeight={400}>{name}</Typography>
                </Box>
              )}
              isOptionEqualToValue={(option, value) =>
                option.code === value.code
              }
              fullWidth
            />
          </div>
        );
      }}
    />
  );
};
